/**
 * Theme constants for FimFam AI
 * Modern, minimal, clean aesthetic with Comfortaa & Noto Sans fonts
 */

import { Platform, ViewStyle } from 'react-native';

/**
 * Pastel Color Palette
 * Soft, warm pastels with excellent text contrast
 */
export const PastelPalette = {
  powderBlush: '#FFADAD',    // pink
  apricotCream: '#FFD6A5',   // orange
  cream: '#FDFFB6',          // yellow
  teaGreen: '#CAFFBF',       // green
  electricAqua: '#9BF6FF',   // cyan
  babyBlueIce: '#2296f3',    // blue (accent)
  periwinkle: '#BDB2FF',     // purple
  mauve: '#FFC6FF',          // pink/purple
  porcelain: '#F9F6F0',      // warm off-white
};

/**
 * Modern Minimal Color Palette
 * Pastel-based with excellent text contrast
 */
export const ModernPalette = {
  // Clean backgrounds
  white: PastelPalette.porcelain,
  offWhite: '#F9F6F0',
  fimCream: PastelPalette.porcelain,
  lightGray: '#F5F5F5',

  // Text colors - dark for contrast against pastels
  textPrimary: '#1A1A1A',
  textSecondary: '#6B6B6B',
  textTertiary: '#9E9E9E',
  textInverse: '#FFFFFF',

  // Subtle borders
  borderLight: '#F0F0F0',
  borderMedium: '#E5E5E5',
  borderDark: '#D0D0D0',

  // Primary accent (baby-blue-ice)
  primary: PastelPalette.babyBlueIce,
  primaryLight: '#64B5F6',
  primaryDark: '#1976D2',

  // Semantic colors (pastel-based)
  success: PastelPalette.teaGreen,
  successLight: '#DFFFDA',
  warning: PastelPalette.apricotCream,
  warningLight: '#FFE5C4',
  danger: '#FF3B30',
  dangerLight: '#FFADAD',
  info: PastelPalette.electricAqua,
  infoLight: '#C5FAFF',

  // Event colors (pastel palette)
  eventBlue: PastelPalette.electricAqua,
  eventPink: PastelPalette.mauve,
  eventOrange: PastelPalette.apricotCream,
  eventYellow: PastelPalette.cream,
  eventLime: PastelPalette.teaGreen,
  eventPurple: PastelPalette.periwinkle,
  eventRose: PastelPalette.powderBlush,

  // Today highlight (subtle)
  todayHighlight: '#EBF6FF',
  selectedDayHighlight: '#EFEAFB',
  selectedTint: 'rgba(34, 150, 243, 0.15)',
};

/**
 * Shadow presets for consistent elevation
 */
export const Shadows: Record<string, ViewStyle> = {
  none: {},
  sm: Platform.select({
    ios: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.05,
      shadowRadius: 2,
    },
    android: {
      elevation: 1,
    },
    default: {},
  }) as ViewStyle,
  md: Platform.select({
    ios: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.08,
      shadowRadius: 4,
    },
    android: {
      elevation: 3,
    },
    default: {},
  }) as ViewStyle,
  lg: Platform.select({
    ios: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.12,
      shadowRadius: 8,
    },
    android: {
      elevation: 6,
    },
    default: {},
  }) as ViewStyle,
  smBottomRight: Platform.select({
    ios: {
      shadowColor: '#000',
      shadowOffset: { width: 2, height: 2 },
      shadowOpacity: 0.05,
      shadowRadius: 1,
    },
    android: {
      elevation: 1,
    },
    default: {},
  }) as ViewStyle,
  mdBottomRight: Platform.select({
    ios: {
      shadowColor: '#000',
      shadowOffset: { width: 3, height: 3 },
      shadowOpacity: 0.08,
      shadowRadius: 2,
    },
    android: {
      elevation: 3,
    },
    default: {},
  }) as ViewStyle,
};

/**
 * Legacy Paper-craft palette (for gradual migration)
 */
export const PaperCraft = {
  paperWhite: '#FEFDFB',
  paperCream: '#FAF8F5',
  paperLight: '#F7F5F2',
  inkBlue: '#4A6FA5',
  inkGrey: '#5A6670',
  inkDark: '#3D4852',
  borderLight: '#E8E4DF',
  borderWarm: '#DED9D3',
  borderSubtle: '#F0ECE8',
  eventBlue: '#70d6ff',
  eventPink: '#ff70a6',
  eventOrange: '#ff9770',
  eventYellow: '#ffd670',
  eventLime: '#e9ff70',
  todayHighlight: '#FFF8E1',
  selectedDayHighlight: '#F1EBFF',
  selectedTint: 'rgba(74, 111, 165, 0.08)',
  shadowColor: 'rgba(74, 111, 165, 0.12)',
};

const AppColors = {
  // Dark Mode Colors - Midnight purple theme
  dark: {
    bgDark: '#000000',      // AMOLED pure black
    bg: '#000000',          // AMOLED pure black main surface
    bgLight: '#1A1726',     // Subtle purple tint for cards
    text: '#FFFFFF',        // Primary text
    textMuted: '#B0B0B0',   // Secondary text (slightly lighter)
    highlight: '#F0A050',   // Bright warm orange
    border: '#2D2640',      // Purple accent border (old bgLight)
    borderMuted: '#1E1B2E', // Subtle border (old bg)
    primary: '#F0A050',     // Bright warm orange (accent)
    secondary: '#D9A8D9',   // Muted mauve
    danger: '#E05252',      // iOS system red (dark mode)
    warning: '#D9A87A',     // Muted apricot-cream
    success: '#8FD98F',     // Muted tea-green
    info: '#7AD9E8',        // Muted electric-aqua
    accent: '#A8A0D9',      // Muted periwinkle
    pink: '#E8A8E8',        // Muted mauve for events
    blue: '#8FDBEB',        // Muted aqua for events
    yellow: '#E8E88F',      // Muted cream for events
    paperWhite: '#1A1726',
    paperCream: '#0D0B14',
    borderLight: '#2D2640',
    todayHighlight: '#1E1B2E',
    selectedDayHighlight: '#2B2344',
  },
  // Light Mode Colors - Modern minimal
  light: {
    bgDark: ModernPalette.fimCream,
    bg: ModernPalette.white,
    bgLight: ModernPalette.white,
    text: ModernPalette.textPrimary,
    textMuted: ModernPalette.textSecondary,
    highlight: ModernPalette.primary,
    border: ModernPalette.borderMedium,
    borderMuted: ModernPalette.borderLight,
    primary: ModernPalette.primary,
    secondary: ModernPalette.eventPink,
    danger: ModernPalette.danger,
    warning: ModernPalette.warning,
    success: ModernPalette.success,
    info: ModernPalette.info,
    accent: ModernPalette.eventLime,
    pink: ModernPalette.eventPink,
    blue: ModernPalette.eventBlue,
    yellow: ModernPalette.eventYellow,
    paperWhite: ModernPalette.white,
    paperCream: ModernPalette.offWhite,
    borderLight: ModernPalette.borderLight,
    todayHighlight: ModernPalette.todayHighlight,
    selectedDayHighlight: ModernPalette.selectedDayHighlight,
  },
};

export const Colors = {
  light: {
    text: AppColors.light.text,
    textMuted: AppColors.light.textMuted,
    background: AppColors.light.bgDark,
    backgroundLight: AppColors.light.bgLight,
    cardBackground: AppColors.light.bgLight,
    tint: AppColors.light.primary,
    icon: AppColors.light.textMuted,
    tabIconDefault: AppColors.light.textMuted,
    tabIconSelected: AppColors.light.primary,
    border: AppColors.light.border,
    borderMuted: AppColors.light.borderMuted,
    primary: AppColors.light.primary,
    secondary: AppColors.light.secondary,
    danger: AppColors.light.danger,
    warning: AppColors.light.warning,
    success: AppColors.light.success,
    info: AppColors.light.info,
    highlight: AppColors.light.highlight,
    accent: AppColors.light.accent,
    pink: AppColors.light.pink,
    blue: AppColors.light.blue,
    yellow: AppColors.light.yellow,
    // Paper-craft additions
    paperWhite: AppColors.light.paperWhite,
    paperCream: AppColors.light.paperCream,
    borderLight: AppColors.light.borderLight,
    todayHighlight: AppColors.light.todayHighlight,
    selectedDayHighlight: AppColors.light.selectedDayHighlight,
    // Full-screen scrims behind the FAB overlays (chat, suggestions, add-by-photo).
    // Light mode dims the screen behind; dark mode must be near-opaque because a
    // translucent black scrim over the pure-black background hides nothing.
    overlayScrim: 'rgba(0, 0, 0, 0.6)',
    chatOverlayScrim: 'rgba(0, 0, 0, 0.3)',
  },
  dark: {
    text: AppColors.dark.text,
    textMuted: AppColors.dark.textMuted,
    background: AppColors.dark.bg,
    backgroundLight: AppColors.dark.bgLight,
    cardBackground: AppColors.dark.bgLight,
    tint: AppColors.dark.primary,
    icon: AppColors.dark.textMuted,
    tabIconDefault: AppColors.dark.textMuted,
    tabIconSelected: AppColors.dark.primary,
    border: AppColors.dark.border,
    borderMuted: AppColors.dark.borderMuted,
    primary: AppColors.dark.primary,
    secondary: AppColors.dark.secondary,
    danger: AppColors.dark.danger,
    warning: AppColors.dark.warning,
    success: AppColors.dark.success,
    info: AppColors.dark.info,
    highlight: AppColors.dark.highlight,
    accent: AppColors.dark.accent,
    pink: AppColors.dark.pink,
    blue: AppColors.dark.blue,
    yellow: AppColors.dark.yellow,
    // Paper-craft additions
    paperWhite: AppColors.dark.paperWhite,
    paperCream: AppColors.dark.paperCream,
    borderLight: AppColors.dark.borderLight,
    todayHighlight: AppColors.dark.todayHighlight,
    selectedDayHighlight: AppColors.dark.selectedDayHighlight,
    // Near-opaque midnight-purple scrim: hides the screen behind the overlay
    // while staying visually distinct from the AMOLED-black background.
    overlayScrim: 'rgba(16, 13, 26, 0.95)',
    chatOverlayScrim: 'rgba(16, 13, 26, 0.95)',
  },
};

export const Fonts = Platform.select({
  ios: {
    /** iOS `UIFontDescriptorSystemDesignDefault` */
    sans: 'system-ui',
    /** iOS `UIFontDescriptorSystemDesignSerif` */
    serif: 'ui-serif',
    /** iOS `UIFontDescriptorSystemDesignRounded` */
    rounded: 'ui-rounded',
    /** iOS `UIFontDescriptorSystemDesignMonospaced` */
    mono: 'ui-monospace',
  },
  default: {
    sans: 'normal',
    serif: 'serif',
    rounded: 'normal',
    mono: 'monospace',
  },
  web: {
    sans: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    serif: "Georgia, 'Times New Roman', serif",
    rounded: "'SF Pro Rounded', 'Hiragino Maru Gothic ProN', Meiryo, 'MS PGothic', sans-serif",
    mono: "SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  },
});
